import { useQuery } from '@tanstack/react-query'
import { type JwtPayload, jwtDecode } from 'jwt-decode'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '@/hooks/useAuth'
import { createSupabaseClient } from '@/integrations/supabase/client'
import { getPortfolioRoleName } from '@/lib/permissions/getPortfolioRoleName'
import { PortfolioRole } from '@/lib/permissions/types'
import {
  portfolioHomeRoute,
  userWorkspaceEntryPath,
} from '@/lib/workspace/routerHelpers'

interface ClerkOrganization {
  id?: string
  rol?: string
  slg?: string
}

interface ClerkJWTPayload extends JwtPayload {
  azp?: string
  email_address?: string
  first_name?: string
  fva?: [number, number]
  last_name?: string
  o?: ClerkOrganization
  org_name?: string
  role?: string
  sid?: string
  sts?: string
  v?: number
}

interface PortfolioUserWithOrganization {
  portfolio_id: string
  portfolios: { organization_id: string }
  portfolio_user_roles?: { name: string } | { name: string }[] | null
}

interface ProductUserWithPortfolio {
  product_id: string
  portfolio_id: string
  portfolios: { organization_id: string }
}

export interface HoldingAssignmentState {
  isPortfolioUser: boolean
  isProductUser: boolean
  workspacePath: string | null
}

const resolveHoldingAssignments = async (
  supabase: ReturnType<typeof createSupabaseClient>,
  userId: string
): Promise<HoldingAssignmentState> => {
  const [portfolioResult, productResult] = await Promise.all([
    supabase
      .from('portfolio_users')
      .select(
        'portfolio_id, portfolios!inner(organization_id), portfolio_user_roles(name)'
      )
      .eq('organization_user_id', userId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true }),
    supabase
      .from('product_users')
      .select('product_id, portfolio_id, portfolios!inner(organization_id)')
      .eq('organization_user_id', userId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true })
      .limit(1),
  ])

  if (portfolioResult.error) {
    throw portfolioResult.error
  }

  if (productResult.error) {
    throw productResult.error
  }

  const portfolioRows = (portfolioResult.data ??
    []) as PortfolioUserWithOrganization[]
  const portfolioIds = new Set(portfolioRows.map((row) => row.portfolio_id))
  const firstPortfolioManagerRow =
    portfolioRows.find(
      (row) =>
        getPortfolioRoleName(row.portfolio_user_roles) ===
        PortfolioRole.PortfolioManager
    ) ?? null
  const isPortfolioUser = portfolioIds.size > 0
  const productRow = (productResult.data?.[0] ??
    null) as ProductUserWithPortfolio | null
  const isProductUser = productRow != null

  let workspacePath: string | null = null

  if (
    isPortfolioUser &&
    isProductUser &&
    productRow &&
    portfolioIds.has(productRow.portfolio_id)
  ) {
    workspacePath = userWorkspaceEntryPath(
      productRow.portfolios.organization_id,
      {
        portfolioId: productRow.portfolio_id,
        productId: productRow.product_id,
      }
    )
  } else if (isPortfolioUser && !isProductUser && firstPortfolioManagerRow) {
    workspacePath = portfolioHomeRoute(
      firstPortfolioManagerRow.portfolios.organization_id,
      firstPortfolioManagerRow.portfolio_id
    )
  }

  return {
    isPortfolioUser,
    isProductUser,
    workspacePath,
  }
}

export const useHolding = () => {
  const [orgName, setOrgName] = useState<string | null>(null)
  const { signOut, getToken, user } = useAuth()
  const navigate = useNavigate()

  const supabaseWithAuth = useMemo(
    () => createSupabaseClient(getToken),
    [getToken]
  )

  const handleCheckAgain = () => {
    window.location.reload()
  }

  useEffect(() => {
    const getUserToken = async () => {
      const token = await getToken()

      if (!token) {
        return
      }

      try {
        const decodedToken = jwtDecode(token) satisfies ClerkJWTPayload

        setOrgName(decodedToken.org_name ?? null)
      } catch (error) {
        console.error('Failed to decode token:', error)
      }
    }

    getUserToken()
  }, [getToken])

  const { data: holdingAssignments, isLoading: isHoldingAssignmentsLoading } =
    useQuery({
      queryKey: ['holdingAssignments', user?.id],
      enabled: !!user?.id,
      queryFn: () => resolveHoldingAssignments(supabaseWithAuth, user!.id),
    })

  return {
    handleCheckAgain,
    isHoldingLoading: isHoldingAssignmentsLoading ?? false,
    isPortfolioUser: holdingAssignments?.isPortfolioUser ?? false,
    isProductUser: holdingAssignments?.isProductUser ?? false,
    workspacePath: holdingAssignments?.workspacePath ?? null,
    navigate,
    orgName,
    signOut,
  }
}
